import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertTriangle, Loader } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import { unsubscribeFromMarketing } from "../services/directoryService";
import "./Directory.css";

/**
 * Landing page for the unsubscribe link in our marketing emails.
 *
 * It acts on load rather than asking for a confirming click: someone who
 * clicked "unsubscribe" has already made the decision, and a second gate is
 * the kind of friction that gets mail marked as spam instead.
 */
export default function Unsubscribe() {
  const { darkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [state, setState] = useState("working");
  const [message, setMessage] = useState("");
  // StrictMode double-invokes effects in development; one request is enough.
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    if (!token) {
      setState("error");
      setMessage("This link is missing its unsubscribe token.");
      return;
    }

    unsubscribeFromMarketing(token)
      .then((res) => {
        setState("done");
        setMessage(
          res.message ||
            "You have been unsubscribed from LawFusion marketing emails.",
        );
      })
      .catch((err) => {
        setState("error");
        setMessage(err.message || "We could not unsubscribe this address.");
      });
  }, [token]);

  const working = state === "working";
  const done = state === "done";

  return (
    <div className={`directory-container ${darkMode ? "dark" : ""}`}>
      <SEO
        title="Unsubscribe | LawFusion"
        path="/unsubscribe"
        description="Unsubscribe from LawFusion marketing emails."
        noIndex={true}
      />
      <Header />

      <main className="directory-content">
        <section
          style={{
            maxWidth: "560px",
            margin: "0 auto",
            padding: "64px 20px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 22px",
              background: done
                ? darkMode
                  ? "rgba(34,197,94,0.18)"
                  : "#dcfce7"
                : darkMode
                  ? "rgba(239,68,68,0.18)"
                  : "#fee2e2",
              color: done ? "#16a34a" : "#dc2626",
            }}
          >
            {working ? (
              <Loader size={26} className="fa-spin" />
            ) : done ? (
              <Check size={30} strokeWidth={2.5} />
            ) : (
              <AlertTriangle size={28} />
            )}
          </div>

          <h1
            style={{
              fontSize: "26px",
              fontWeight: 800,
              margin: "0 0 12px",
              color: darkMode ? "#f8fafc" : "#1a2456",
            }}
          >
            {working
              ? "Unsubscribing…"
              : done
                ? "You're Unsubscribed"
                : "We Couldn't Unsubscribe You"}
          </h1>

          <p
            style={{
              fontSize: "15.5px",
              lineHeight: 1.5,
              margin: "0 0 32px",
              color: darkMode ? "#94a3b8" : "#6b7280",
            }}
          >
            {working
              ? "This will only take a moment."
              : done
                ? `${message} You will still receive essential emails about your own listing, such as approval and account notices.`
                : message}
          </p>

          {!working && (
            <Link
              to="/directory"
              className="btn-join"
              style={{ textDecoration: "none", display: "inline-block" }}
            >
              Back to the Directory
            </Link>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
